/**
 * Comprehensive URL validation and normalization utilities
 * Prevents double-protocol issues and ensures consistent URL handling across the app
 */

export interface URLValidationResult {
  isValid: boolean;
  normalizedUrl: string;
  error?: string;
  protocol?: string;
  hostname?: string;
  pathname?: string;
}

/**
 * Normalizes and validates a URL, preventing common issues like double protocols
 */
export function validateAndNormalizeUrl(input: string): URLValidationResult {
  console.log(`[URL Utils] Starting validation for input: "${input}"`);
  
  if (!input || typeof input !== 'string') {
    console.log(`[URL Utils] Invalid input: empty or non-string`);
    return {
      isValid: false,
      normalizedUrl: '',
      error: 'URL is required'
    };
  }

  // Clean the input first
  let cleanInput = input.trim();
  
  // Remove multiple consecutive spaces
  cleanInput = cleanInput.replace(/\s+/g, ' ');
  
  // Fix common double-protocol issues
  cleanInput = cleanInput.replace(/^https?:\/\/\s+https?:\/\//, 'https://');
  cleanInput = cleanInput.replace(/^https?:\/\/\s+/, 'https://');
  
  console.log(`[URL Utils] After cleaning: "${cleanInput}"`);

  // If it doesn't start with a protocol, add https://
  if (!cleanInput.match(/^https?:\/\//i)) {
    cleanInput = `https://${cleanInput}`;
    console.log(`[URL Utils] Added protocol: "${cleanInput}"`);
  }

  try {
    const urlObject = new URL(cleanInput);
    
    // Ensure protocol is lowercase
    const protocol = urlObject.protocol.toLowerCase();
    if (protocol !== 'http:' && protocol !== 'https:') {
      return {
        isValid: false,
        normalizedUrl: '',
        error: 'Only HTTP and HTTPS protocols are supported'
      };
    }

    // Build normalized URL
    const normalizedUrl = `${protocol}//${urlObject.hostname}${urlObject.pathname}${urlObject.search}${urlObject.hash}`;
    
    console.log(`[URL Utils] Successfully normalized: "${normalizedUrl}"`);
    
    return {
      isValid: true,
      normalizedUrl,
      protocol: urlObject.protocol,
      hostname: urlObject.hostname,
      pathname: urlObject.pathname
    };
  } catch (error) {
    console.log(`[URL Utils] URL parsing failed:`, error);
    return {
      isValid: false,
      normalizedUrl: '',
      error: `Invalid URL format: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

/**
 * Quick URL validation without normalization
 */
export function isValidUrl(url: string): boolean {
  const result = validateAndNormalizeUrl(url);
  return result.isValid;
}

/**
 * Extracts domain from URL
 */
export function extractDomain(url: string): string | null {
  const result = validateAndNormalizeUrl(url);
  return result.isValid ? result.hostname || null : null;
}

/**
 * Formats URL for display (removes protocol for cleaner display)
 */
export function formatUrlForDisplay(url: string): string {
  const result = validateAndNormalizeUrl(url);
  if (!result.isValid) return url;
  
  return result.normalizedUrl.replace(/^https?:\/\//, '');
}

/**
 * Debug function to trace URL transformations
 */
export function debugUrlTransformation(input: string, context: string = 'unknown'): void {
  console.group(`[URL Debug] ${context}`);
  console.log(`Input: "${input}"`);
  const result = validateAndNormalizeUrl(input);
  console.log(`Valid: ${result.isValid}`);
  console.log(`Normalized: "${result.normalizedUrl}"`);
  if (result.error) {
    console.log(`Error: ${result.error}`);
  }
  console.groupEnd();
}